import React from 'react';
import { Tree as AntTree, TreeProps as AntTreeProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

const { TreeNode: AntTreeNode } = AntTree;

export interface TreeProps extends AntTreeProps {
  className?: string;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
  tree: {
    '&.ant-tree': {
      backgroundColor: 'transparent',
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
    },
    // Tree node styling
    '& .ant-tree-treenode': {
      padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: theme.color.semantic.background.neutral.subtle,
      },
    },
    // Selected node
    '& .ant-tree-treenode-selected': {
      backgroundColor: theme.color.semantic.brand.default.light,
      '& .ant-tree-title': {
        color: theme.color.semantic.brand.default.primary,
      },
      '& .ant-tree-node-content-wrapper': {
        backgroundColor: 'transparent',
      },
    },
    // Disabled node
    '& .ant-tree-treenode-disabled': {
      color: theme.color.semantic.text.soft,
      cursor: 'not-allowed',
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '& .ant-tree-title': {
        color: theme.color.semantic.text.soft,
      },
    },
    // Tree title
    '& .ant-tree-title': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
    },
    // Switcher icon (expand/collapse)
    '& .ant-tree-switcher': {
      color: theme.color.semantic.text.secondary,
      '&:hover': {
        color: theme.color.semantic.brand.default.primary,
      },
    },
    // Node icons
    '& .ant-tree-iconEle': {
      color: theme.color.semantic.text.primary,
    },
    '& .ant-tree-treenode:hover .ant-tree-iconEle': {
      color: theme.color.semantic.text.primary,
    },
    '& .ant-tree-treenode-selected .ant-tree-iconEle': {
      color: theme.color.semantic.brand.default.primary,
    },
    '& .ant-tree-treenode-disabled .ant-tree-iconEle': {
      color: theme.color.semantic.text.soft,
    },
  },
  treeCheckable: {
    // Checkbox inner styling
    '& .ant-tree-checkbox-inner': {
      width: theme.spacing[4],
      height: theme.spacing[4],
      borderColor: `${theme.color.semantic.border.primary} !important`,
      borderWidth: theme.border.width.sm,
      borderRadius: theme.radius.xs,
      backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
      transition: 'all 0.2s ease',
    },
    // Hover state
    '& .ant-tree-checkbox:hover .ant-tree-checkbox-inner': {
      borderColor: `${theme.color.semantic.brand.default.secondary} !important`,
    },
    // Checked state
    '& .ant-tree-checkbox-checked .ant-tree-checkbox-inner': {
      backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
      borderColor: `${theme.color.semantic.brand.default.primary} !important`,
      '&:after': {
        borderColor: `${theme.color.semantic.border.inverse} !important`,
      },
    },
    // Checked hover state
    '& .ant-tree-checkbox-checked:hover .ant-tree-checkbox-inner': {
      backgroundColor: `${theme.color.semantic.brand.default.secondary} !important`,
      borderColor: `${theme.color.semantic.brand.default.secondary} !important`,
      '&:after': {
        borderColor: `${theme.color.semantic.border.inverse} !important`,
      },
    },
    // Indeterminate state
    '& .ant-tree-checkbox-indeterminate .ant-tree-checkbox-inner': {
      backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
      borderColor: `${theme.color.semantic.brand.default.primary} !important`,
      '&:after': {
        backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
        border: 'none',
        height: theme.border.width.md,
        width: theme.spacing[2],
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      },
    },
    // Indeterminate hover state
    '& .ant-tree-checkbox-indeterminate:hover .ant-tree-checkbox-inner': {
      backgroundColor: `${theme.color.semantic.brand.default.secondary} !important`,
      borderColor: `${theme.color.semantic.brand.default.secondary} !important`,
      '&:after': {
        backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
      },
    },
    // Focus state
    '& .ant-tree-checkbox-input:focus + .ant-tree-checkbox-inner': {
      borderColor: `${theme.color.semantic.brand.default.primary} !important`,
    },
    // Disabled state
    '& .ant-tree-checkbox-disabled .ant-tree-checkbox-inner': {
      backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
      borderColor: `${theme.color.semantic.border.subtle} !important`,
      cursor: 'not-allowed',
    },
    '& .ant-tree-checkbox-disabled': {
      cursor: 'not-allowed',
      '& .ant-tree-checkbox-input': {
        cursor: 'not-allowed',
      },
    },
    // Disabled checked state
    '& .ant-tree-checkbox-disabled.ant-tree-checkbox-checked .ant-tree-checkbox-inner': {
      backgroundColor: `${theme.color.semantic.background.neutral.strong} !important`,
      borderColor: `${theme.color.semantic.border.primary} !important`,
      '&:after': {
        borderColor: `${theme.color.semantic.border.primary} !important`,
        borderWidth: theme.border.width.sm,
      },
    },
    // Disabled indeterminate state
    '& .ant-tree-checkbox-disabled.ant-tree-checkbox-indeterminate .ant-tree-checkbox-inner': {
      backgroundColor: `${theme.color.semantic.background.neutral.strong} !important`,
      borderColor: `${theme.color.semantic.border.primary} !important`,
      '&:after': {
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        border: 'none',
        height: theme.border.width.md,
        width: theme.spacing[2],
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        visibility: 'visible',
      },
    },
  },
  treeDraggable: {
    '& .ant-tree-treenode-draggable': {
      cursor: 'move',
    },
    '& .ant-tree-treenode.drag-over': {
      backgroundColor: theme.color.semantic.brand.default.subtle,
      borderColor: theme.color.semantic.brand.default.primary,
    },
    '& .ant-tree-treenode.drag-over-gap-top': {
      borderTop: `${theme.border.width.sm} solid ${theme.color.semantic.brand.default.primary}`,
    },
    '& .ant-tree-treenode.drag-over-gap-bottom': {
      borderBottom: `${theme.border.width.sm} solid ${theme.color.semantic.brand.default.primary}`,
    },
  },
  treeShowIcon: {
    '& .ant-tree-node-content-wrapper': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing[1],
    },
  },
}));

export const Tree: React.FC<TreeProps> & {
  TreeNode: typeof AntTreeNode;
} = ({
  checkable = false,
  draggable = false,
  showIcon = false,
  className = '',
  ...rest
}) => {
  const classes = useStyles();

  const getVariantClass = () => {
    const classNames = [];
    if (checkable) classNames.push(classes.treeCheckable);
    if (draggable) classNames.push(classes.treeDraggable);
    if (showIcon) classNames.push(classes.treeShowIcon);
    return classNames.join(' ');
  };

  const combinedClassName = `${classes.tree} ${getVariantClass()} ${className}`.trim();

  return (
    <AntTree
      checkable={checkable}
      draggable={draggable}
      showIcon={showIcon}
      {...rest}
      className={combinedClassName}
    />
  );
};

// Attach TreeNode subcomponent
Tree.TreeNode = AntTreeNode;

export const TreeNode = AntTreeNode;

export default Tree;
